import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './Dropdown.module.scss';
import DropdownMenu from './DropdownMenu';

export const useDropdownEvents = (ref, isOpen, initialElement) => {
  const [open, setOpen] = useState(isOpen || false);
  const [currentElement, setCurrentElement] = useState(initialElement);

  const handleOutsideClick = (e) => {
    if (!(ref || ref.current)) return;
    // A target detached from the DOM during this render cycle (e.g. a react-select option removed
    // on select, or a field remounted by an autosave) was inside the menu — not a genuine outside click.
    if (e.target instanceof Node && !e.target.isConnected) return;
    if (ref.current.contains(e.target)) return;

    setOpen(false);
  };

  return { currentElement, handleOutsideClick, open, setCurrentElement, setOpen };
};

export const useDropdownEventsPropTypes = PropTypes.shape({
  handleOutsideClick: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
});

export const useClickOutside = (handler) => {
  useEffect(() => {
    document.addEventListener('click', handler, false);

    return () => document.removeEventListener('click', handler, false);
  }, []);
};

const Dropdown = ({
  ariaLabel, buttonClass, children, containerClass, dropdownClass,
  dropdownPosition, menuRole, onOpenChange, render, toggleText, isOpen, hasButton,
}) => {
  const ref = useRef();
  const { handleOutsideClick, open, setOpen } = useDropdownEvents(ref, isOpen);
  useClickOutside(handleOutsideClick);

  useEffect(() => { onOpenChange(open); }, [open, onOpenChange]);

  // Menu semantics require owned menuitems; disclosure-style dropdowns opt out.
  const menuRoleValue = menuRole ? 'menu' : null;

  const renderChildren = () => {
    if (render) return render({ closeModal: () => setOpen(false), isOpen: open });

    return (
      React.Children.map(children, (child) => {
        if (!child) return child;
        if (child && !child.props['data-closeonclick']) return child;

        return React.cloneElement(child, {
          onClick: (e) => {
            if (child.props.onClick) child.props.onClick(e);

            setOpen(false);
          },
        });
      })
    );
  };

  return (
    <div ref={ref} className={`${styles.container} ${containerClass}`}>
      {hasButton && (
        <button
          aria-expanded={open}
          aria-label={ariaLabel}
          className={`${buttonClass} ${styles.button}`}
          type="button"
          onClick={() => setOpen(prev => !prev)}
        >
          {typeof toggleText === 'function' ? toggleText({ setOpen }) : toggleText}
        </button>
      )}
      <DropdownMenu className={dropdownClass} open={open} position={dropdownPosition} role={menuRoleValue}>
        {renderChildren()}
      </DropdownMenu>
    </div>
  );
};

Dropdown.defaultProps = {
  ariaLabel: '',
  buttonClass: 'btn btn--outline-purple dropdown-toggle',
  children: null,
  containerClass: '',
  dropdownClass: '',
  dropdownPosition: 'bottom',
  hasButton: true,
  isOpen: false,
  menuRole: true,
  onOpenChange: () => {},
  render: null,
  toggleText: 'Toggle',
};

Dropdown.propTypes = {
  ariaLabel: PropTypes.string,
  buttonClass: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  containerClass: PropTypes.string,
  dropdownClass: PropTypes.string,
  dropdownPosition: PropTypes.oneOf([
    'top',
    'right',
    'bottom',
    'left',
    'top-end',
    'bottom-start',
  ]),
  hasButton: PropTypes.bool,
  isOpen: PropTypes.bool,
  menuRole: PropTypes.bool,
  onOpenChange: PropTypes.func,
  render: PropTypes.func,
  toggleText: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.string,
  ]),
};

export default Dropdown;
